/**
 * Tron 能量租赁 API 适配层
 * 对接 payment-gateway /energy-rental/* 真实接口，支持 Mock 开关
 */

import { buildTronEnergyOrderSearchParams } from '@/utils/tronEnergySearch';
import { get, post } from '../client';
import type { ApiResult } from '../types/common';
import type {
  CreateTronEnergyOrderRequest,
  CreateTronEnergyOrderChallengeRequest,
  CreateTronEnergyOrderResult,
  AuthorizeTronEnergyOrderRequest,
  AuthorizeTronEnergyOrderResult,
  EnableTronEnergyChallengeResult,
  EnableTronEnergyRequest,
  TronEnergyConfig,
  TronEnergyCostEstimate,
  TronEnergyOrder,
  TronEnergyOrdersQuery,
  TronEnergyOrdersResult,
  TronEnergyQuoteRequest,
  TronEnergyServiceStatus,
  TronEnergySummary,
} from './tronEnergy';
import {
  mapChallenge,
  mapConfig,
  mapCreateOrderResult,
  mapOrderDetail,
  mapOrdersResult,
  mapQuote,
  mapServiceStatus,
  mapSummary,
  toApiOrderStatus,
  type ApiEnergyRentalCreateOrderData,
  type ApiEnergyRentalOrdersData,
  type ApiEnergyRentalOverviewData,
  type ApiEnergyRentalQuoteData,
  type ApiEnergyRentalStatusData,
  type EnergyRentalAction,
} from './tronEnergy.mapper';
import {
  mockAuthorizeTronEnergyOrder,
  mockCreateTronEnergyOrder,
  mockEnableTronEnergy,
  mockGetEnergyRentalChallenge,
  mockGetTronEnergyConfig,
  mockGetTronEnergyOrder,
  mockGetTronEnergyOrders,
  mockGetTronEnergyQuote,
  mockGetTronEnergyStatus,
  mockGetTronEnergySummary,
} from '../mocks/tronEnergy.mock';

const USE_MOCK = import.meta.env.VITE_USE_TRON_ENERGY_MOCK === 'true';

const ENERGY_RENTAL_JWT = {
  requiresAuth: true,
} as const;

function buildQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

function createIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tron-order-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function postEnergyRentalChallenge(
  params: {
    appId: string;
    action: EnergyRentalAction;
    receiverAddress?: string;
    energyAmount?: number;
    durationHours?: number;
  },
): Promise<ApiResult<EnableTronEnergyChallengeResult>> {
  if (USE_MOCK) {
    return mockGetEnergyRentalChallenge(params);
  }
  const result = await post<{ challengeId: string; message: string; expiresAt: number }>(
    '/energy-rental/challenge',
    params,
    ENERGY_RENTAL_JWT,
  );
  if (!result.ok) return result;
  return { ok: true, data: mapChallenge(result.data) };
}

/**
 * 获取应用 Tron 能量租赁服务状态
 */
export async function getTronEnergyStatus(appId: string): Promise<ApiResult<TronEnergyServiceStatus>> {
  if (USE_MOCK) {
    return mockGetTronEnergyStatus(appId);
  }
  const result = await get<ApiEnergyRentalStatusData>(
    `/energy-rental/status${buildQuery({ appId })}`,
    ENERGY_RENTAL_JWT,
  );
  if (!result.ok) return result;
  return { ok: true, data: mapServiceStatus(result.data, appId) };
}

/**
 * 获取 Tron 能量租赁配置（档位、文档链接等）
 */
export async function getTronEnergyConfig(appId: string): Promise<ApiResult<TronEnergyConfig>> {
  if (USE_MOCK) {
    return mockGetTronEnergyConfig();
  }
  const result = await get<ApiEnergyRentalStatusData>(
    `/energy-rental/status${buildQuery({ appId })}`,
    ENERGY_RENTAL_JWT,
  );
  if (!result.ok) return result;
  return { ok: true, data: mapConfig(result.data) };
}

/**
 * 获取应用 Tron 能量租赁统计与账户余额
 */
export async function getTronEnergySummary(appId: string): Promise<ApiResult<TronEnergySummary>> {
  if (USE_MOCK) {
    return mockGetTronEnergySummary(appId);
  }
  const result = await get<ApiEnergyRentalOverviewData>(
    `/energy-rental/overview${buildQuery({ appId })}`,
    ENERGY_RENTAL_JWT,
  );
  if (!result.ok) return result;
  return { ok: true, data: mapSummary(result.data) };
}

/**
 * Quote 余额不足时网关仍返回可渲染的 data（HTTP 2xx + ok:false）。
 * 业务码 50001 / INSUFFICIENT_CREDITS：按成功报价映射，sufficient 走 data.sufficient。
 */
function extractInsufficientCreditsQuoteData(
  result: ApiResult<ApiEnergyRentalQuoteData>,
): ApiEnergyRentalQuoteData | null {
  if (result.ok) return null;

  const raw = result as ApiResult<ApiEnergyRentalQuoteData> & {
    code?: number;
    data?: ApiEnergyRentalQuoteData;
  };
  const isInsufficient =
    raw.code === 50001 || raw.error?.code === 'INSUFFICIENT_CREDITS';
  const data = raw.data;
  if (
    !isInsufficient ||
    !data ||
    typeof data.creditsRequired !== 'number' ||
    typeof data.creditsToDeduct !== 'number' ||
    typeof data.sufficient !== 'boolean'
  ) {
    return null;
  }
  return data;
}

/**
 * 获取 Tron 能量租赁报价
 */
export async function getTronEnergyQuote(
  params: TronEnergyQuoteRequest,
): Promise<ApiResult<TronEnergyCostEstimate>> {
  if (USE_MOCK) {
    return mockGetTronEnergyQuote(params);
  }
  const result = await post<ApiEnergyRentalQuoteData>(
    '/energy-rental/quote',
    {
      energyAmount: params.energyAmount,
      durationHours: params.durationHours ?? 1,
      receiverAddress: params.receiverAddress || undefined,
      appId: params.appId,
    },
    ENERGY_RENTAL_JWT,
  );
  if (result.ok) {
    return { ok: true, data: mapQuote(result.data) };
  }

  const insufficientQuote = extractInsufficientCreditsQuoteData(result);
  if (insufficientQuote) {
    return { ok: true, data: mapQuote(insufficientQuote) };
  }

  return result;
}

/**
 * 获取 Tron 能量租赁订单列表
 */
export async function getTronEnergyOrders(
  params: TronEnergyOrdersQuery,
): Promise<ApiResult<TronEnergyOrdersResult>> {
  if (USE_MOCK) {
    return mockGetTronEnergyOrders(params);
  }

  const search = {
    ...buildTronEnergyOrderSearchParams(params.keyword),
    ...(params.orderNo ? { orderNo: params.orderNo } : {}),
    ...(params.receiverAddress ? { receiverAddress: params.receiverAddress } : {}),
  };

  const query = buildQuery({
    appId: params.appId,
    page: params.page,
    pageSize: params.limit || 50,
    status: toApiOrderStatus(params.status),
    orderNo: search.orderNo,
    receiverAddress: search.receiverAddress,
  });

  const result = await get<ApiEnergyRentalOrdersData>(`/energy-rental/orders${query}`, ENERGY_RENTAL_JWT);
  if (!result.ok) return result;
  return { ok: true, data: mapOrdersResult(result.data) };
}

/**
 * 按平台订单号查询当前 App 下的订单详情
 */
export async function getTronEnergyOrder(
  appId: string,
  orderNo: string,
): Promise<ApiResult<TronEnergyOrder>> {
  if (USE_MOCK) {
    return mockGetTronEnergyOrder(appId, orderNo);
  }
  const result = await get<ApiEnergyRentalCreateOrderData>(
    `/energy-rental/orders/${encodeURIComponent(orderNo)}${buildQuery({ appId })}`,
    ENERGY_RENTAL_JWT,
  );
  if (!result.ok) return result;
  return { ok: true, data: mapOrderDetail(result.data) };
}

/**
 * 获取启用 Tron 能量租赁的钱包签名挑战
 */
export async function getEnableTronEnergyChallenge(
  appId: string,
): Promise<ApiResult<EnableTronEnergyChallengeResult>> {
  return postEnergyRentalChallenge({ appId, action: 'enable' });
}

/**
 * 获取创建租赁订单的钱包签名挑战
 */
export async function getCreateTronEnergyOrderChallenge(
  params: CreateTronEnergyOrderChallengeRequest,
): Promise<ApiResult<EnableTronEnergyChallengeResult>> {
  return postEnergyRentalChallenge({
    ...params,
    action: 'rent',
    durationHours: params.durationHours ?? 1,
  });
}

/**
 * 提交钱包签名启用 Tron 能量租赁服务
 */
export async function enableTronEnergy(
  data: EnableTronEnergyRequest,
): Promise<ApiResult<TronEnergyServiceStatus>> {
  if (USE_MOCK) {
    return mockEnableTronEnergy(data);
  }
  const result = await post<ApiEnergyRentalStatusData>(
    '/energy-rental/enable',
    {
      appId: data.appId,
      challengeId: data.challengeId,
      signature: data.signature,
    },
    ENERGY_RENTAL_JWT,
  );
  if (!result.ok) return result;
  return { ok: true, data: mapServiceStatus(result.data, data.appId) };
}

/**
 * 提交 rent 签名并获取一次性下单凭证
 */
export async function authorizeTronEnergyOrder(
  data: AuthorizeTronEnergyOrderRequest,
): Promise<ApiResult<AuthorizeTronEnergyOrderResult>> {
  if (USE_MOCK) {
    return mockAuthorizeTronEnergyOrder(data);
  }
  return post<AuthorizeTronEnergyOrderResult>(
    '/energy-rental/authorize-order',
    data,
    ENERGY_RENTAL_JWT,
  );
}

/**
 * 提交 authorizationId 并创建 Tron 能量租赁订单
 */
export async function createTronEnergyOrder(
  data: CreateTronEnergyOrderRequest,
): Promise<ApiResult<CreateTronEnergyOrderResult>> {
  if (USE_MOCK) {
    return mockCreateTronEnergyOrder(data);
  }

  const result = await post<ApiEnergyRentalCreateOrderData>(
    '/energy-rental/orders',
    {
      energyAmount: data.energyAmount,
      durationHours: data.durationHours,
      receiverAddress: data.receiverAddress,
      appId: data.appId,
      authorizationId: data.authorizationId,
    },
    {
      ...ENERGY_RENTAL_JWT,
      headers: {
        'Idempotency-Key': data.idempotencyKey || createIdempotencyKey(),
      },
    },
  );
  if (!result.ok) return result;
  return { ok: true, data: mapCreateOrderResult(result.data) };
}
